import { useState, useEffect } from 'react';
import TopAppBar from '../../global/TopBar';
import AdvertSideBar from '../../global/AdvertsSideBar';
import HeaderPage from '../../../components/Header';
import { Box, Typography, useTheme, TextField, Button, IconButton } from '@mui/material';
import { tokens } from '../../../theme';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import DesignServicesOutlinedIcon from '@mui/icons-material/DesignServicesOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined';
import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { useAuth } from '../../../AuthContext';

const Broadcast = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, updateData, deleteData, addData, userID, userRole } = useAuth();

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [editingMessage, setEditingMessage] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch messages on mount and every 10 seconds
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const response = await getData('proccess.php', 'broadcast_V2');
        if (response.success) {
          setMessages(response.data || []);
          setError('');
        } else {
          setError('Failed to fetch messages: ' + response.message);
        }
      } catch (err) {
        setError('Error fetching messages: ' + err.message);
      }
    };

    fetchMessages();
    const intervalId = setInterval(fetchMessages, 10000);

    return () => clearInterval(intervalId);
  }, [getData]);

  // Handle form submission for adding or updating a message
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) {
      setError('Message cannot be empty');
      return;
    }

    try {
      let response;
      if (editingMessage) {
        // Update existing message
        response = await updateData('proccess.php', 'broadcast_V2', { message: newMessage }, editingMessage.id);
        if (response.success) {
          setMessages(messages.map((msg) => (msg.id === editingMessage.id ? { ...msg, message: newMessage } : msg)));
          setSuccess('Message updated successfully');
          setEditingMessage(null);
        } else {
          setError('Failed to update message: ' + response.message);
        }
      } else {
        // Add new message
        response = await addData('proccess.php', 'broadcast_V2', { senderID: userID, message: newMessage, status: 0 });
        if (response.success) {
          setMessages([...messages, { id: response.data.id, senderID: userID, message: newMessage, status: 0, date_created: new Date().toISOString() }]);
          setSuccess('Message added successfully');
        } else {
          setError('Failed to add message: ' + response.message);
        }
      }
      setNewMessage('');
      setError('');
    } catch (err) {
      setError('Error saving message: ' + err.message);
    }
  };

  // Handle edit button click
  const handleEdit = (message) => {
    setEditingMessage(message);
    setNewMessage(message.message);
    setError('');
    setSuccess('');
  };

  // Handle delete button click
  const handleDelete = async (messageId) => {
    try {
      const response = await deleteData('proccess.php', 'broadcast_V2', messageId);
      if (response.success) {
        setMessages(messages.filter((msg) => msg.id !== messageId));
        setSuccess('Message deleted successfully');
      } else {
        setError('Failed to delete message: ' + response.message);
      }
    } catch (err) {
      setError('Error deleting message: ' + err.message);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };

  // Check if user is authorized to perform CRUD actions 
  const isAuthorized = userRole !== 'receptionist' && userRole !== 'user';

  return (
    <Box className="w-100 flex-raw">
      <AdvertSideBar />
      <Box className="w-100">
        {/* Top Header */}
        <TopAppBar />

        {/* Welcoming section */}
        <Box
          mb="10px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="100%"
          bgcolor={colors.dark[400]}
          height="10vh"
        >
          <Box>
            <Typography
              variant={{xs: 'h4', sm: 'h3', md: 'h1'}}
              color={colors.blue[400]}
              fontWeight="bold"
              sx={{ mb: '5px' }}
            >
              Welcome to Mindsinaction's Broadcast
            </Typography>
          </Box>
        </Box>

        <Box p="1rem">
          {/* Broadcast management */}
          <Tabs defaultActiveKey="view" id="broadcast-tabs" className="mb-3">
            <Tab eventKey="view" title="View Messages" icon={<ViewModuleOutlinedIcon />}>
              <Box>
                {error && (
                  <Typography color="error" mb={2}>
                    {error}
                  </Typography>
                )}
                {success && (
                  <Typography color={colors.green[400]} mb={2}>
                    {success}
                  </Typography>
                )}
                {messages.length === 0 ? (
                  <Typography>No messages available</Typography>
                ) : (
                  messages.map((message) => (
                    <Box
                      key={message.id}
                      p={2}
                      mb={1}
                      bgcolor={message.status === 0 ? colors.primary[600] : colors.primary[500]}
                      borderRadius="4px"
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <Typography variant="body1">{message.message}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatDate(message.date_created)} by User {message.senderID}
                        </Typography>
                      </Box>
                      {isAuthorized && (
                        <Box>
                          <IconButton onClick={() => handleEdit(message)}>
                            <EditOutlinedIcon />
                          </IconButton>
                          <IconButton onClick={() => handleDelete(message.id)}>
                            <DeleteOutlinedIcon />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  ))
                )}
              </Box>
            </Tab>
            
              <Tab eventKey="add" title="Add Message" icon={<DesignServicesOutlinedIcon />}>
                <Box component="form" onSubmit={handleSubmit} p={2}>
                  <Typography variant="h6" mb={2}>
                    {editingMessage ? 'Edit Message' : 'Add New Message'}
                  </Typography>
                  {error && (
                    <Typography color="error" mb={2}>
                      {error}
                    </Typography>
                  )}
                  {success && (
                    <Typography color={colors.green[400]} mb={2}>
                      {success}
                    </Typography>
                  )}
                  <TextField
                    fullWidth
                    label="Message"
                    multiline
                    rows={4}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                  <Box display="flex" gap={2}>
                    <Button type="submit" variant="contained" color="primary">
                      {editingMessage ? 'Update' : 'Add'} Message
                    </Button>
                    {editingMessage && (
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => {
                          setEditingMessage(null);
                          setNewMessage('');
                          setError('');
                          setSuccess('');
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                  </Box>
                </Box>
              </Tab>
            
          </Tabs>
        </Box>
      </Box>
    </Box>
  );
};

export default Broadcast;